import type {
    SketchConstraintFormulaCompileContext,
    SketchConstraintFormulaCompileResult,
    SketchConstraintFormulaCompiler,
} from '../../../../types/sketch/solver/SketchConstraintFormulaCompiler';
import type {
    SketchParametricPointHandle,
    SketchParametricSplineCurveDescriptor,
    SketchParametricSplinePointEvaluation,
} from '../../../../types/sketch/solver/SketchParametricTypes';
import {SketchParametricSplineEvaluator} from '../core/SketchParametricSplineEvaluator';
import {SketchParametricEquationFactory} from './SketchParametricEquationFactory';

/** 外部样条端点接触参数粗搜索分段数。 */
const EXTERNAL_SPLINE_SEED_SAMPLE_COUNT = 32;

/**
 * 外部引用固定边界公式编译器。
 *
 * 外部引用约束对象只负责输出目标草图局部坐标中的 plain-data 稳定边界；本编译器
 * 把边界注册为固定参数并与目标实体建立方程，不读取来源对象、Viewer 或引用仓库。
 */
export class SketchExternalReferenceFormulaCompiler implements SketchConstraintFormulaCompiler {
    /** 外部边引用统一使用 OnEdge 关系类型。 */
    public readonly relationKinds = ['OnEdge'] as const;

    /** 外部样条参数求值器。 */
    private readonly splineEvaluator = new SketchParametricSplineEvaluator();

    /** 外部样条最近参数粗搜索复用点。 */
    private readonly splineSeedPoint: SketchParametricSplinePointEvaluation = {x: 0, y: 0};

    /**
     * 编译外部引用关系。
     *
     * @param context 编译上下文。
     * @returns 编译结果。
     */
    public compile(
        context: SketchConstraintFormulaCompileContext,
    ): SketchConstraintFormulaCompileResult {
        if (context.constraintState.relationKind !== 'OnEdge') {
            return 'not-applicable';
        }

        const payload = context.constraintState.formulaPayload;

        if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
            context.reportUnsupported('活动外部引用关系缺少参数公式载荷');
            return 'rejected';
        }

        const kind = Reflect.get(payload, 'kind');

        if (kind === 'external-line') {
            return this.compileExternalLine(context, payload);
        }
        if (kind === 'external-circle') {
            return this.compileExternalCircle(context, payload);
        }
        if (kind === 'external-ellipse') {
            return this.compileExternalEllipse(context, payload);
        }
        if (kind === 'external-parabola') {
            return this.compileExternalParabola(context, payload);
        }
        if (kind === 'external-spline') {
            return this.compileExternalSpline(context, payload);
        }

        context.reportUnsupported(`尚未迁移的外部引用公式载荷：${String(kind)}`);
        return 'rejected';
    }

    /**
     * 编译目标直线两个端点分别位于固定外部无限直线上的关系。
     *
     * 两端点必须分别保留沿载体直线的一维自由度。若使用“平行 + 单端点在线”表达，
     * 斜向载体会在拖拽偏好求解中耦合两端，导致拖动一个端点时整条线沿载体平移。
     *
     * @param context 编译上下文。
     * @param payload 外部直线载荷。
     * @returns 编译结果。
     */
    private compileExternalLine(
        context: SketchConstraintFormulaCompileContext,
        payload: object,
    ): SketchConstraintFormulaCompileResult {
        const entityId = context.resolveDirectEntityIds()[0];
        const targetLine = entityId ? context.linePointHandles.get(entityId) : undefined;
        const start = this.readVector2(Reflect.get(payload, 'start'));
        const end = this.readVector2(Reflect.get(payload, 'end'));

        if (!targetLine || !start || !end) {
            context.reportUnsupported('外部直线引用缺少目标直线或有效局部端点');
            return 'rejected';
        }

        if (Math.hypot(end[0] - start[0], end[1] - start[1]) <= Number.EPSILON) {
            context.reportUnsupported('外部直线引用边界退化为零长度');
            return 'rejected';
        }

        const equationId = `constraint:${context.constraintState.constraintId}`;
        const externalStart = context.system.addPoint(`${equationId}:external-start`, start[0], start[1], true);
        const externalEnd = context.system.addPoint(`${equationId}:external-end`, end[0], end[1], true);

        context.system.addEquation(SketchParametricEquationFactory.nondegenerateLineGuard(
            `${equationId}:target-line-nondegenerate`,
            targetLine[0],
            targetLine[1],
        ));
        context.system.addEquation(SketchParametricEquationFactory.pointOnLine(
            `${equationId}:start-on-line`,
            targetLine[0],
            externalStart,
            externalEnd,
        ));
        context.system.addEquation(SketchParametricEquationFactory.pointOnLine(
            `${equationId}:end-on-line`,
            targetLine[1],
            externalStart,
            externalEnd,
        ));
        return 'compiled';
    }

    /**
     * 编译目标圆与固定外部圆重合。
     *
     * @param context 编译上下文。
     * @param payload 外部圆载荷。
     * @returns 编译结果。
     */
    private compileExternalCircle(
        context: SketchConstraintFormulaCompileContext,
        payload: object,
    ): SketchConstraintFormulaCompileResult {
        const entityId = context.resolveDirectEntityIds()[0];
        const targetCenter = entityId ? context.centerPointHandles.get(entityId) : undefined;
        const radiusParameterId = entityId ? context.radiusParameterIds.get(entityId) : undefined;
        const center = this.readVector2(Reflect.get(payload, 'center'));
        const radius = Reflect.get(payload, 'radius');

        if (!targetCenter || !radiusParameterId || !center
            || typeof radius !== 'number' || !Number.isFinite(radius) || radius <= 0) {
            context.reportUnsupported('外部圆引用缺少目标圆或有效局部圆定义');
            return 'rejected';
        }

        const equationId = `constraint:${context.constraintState.constraintId}`;
        const externalCenter = context.system.addPoint(
            `${equationId}:external-center`,
            center[0],
            center[1],
            true,
        );

        context.system.addEquation(SketchParametricEquationFactory.coincident(
            `${equationId}:center`,
            targetCenter,
            externalCenter,
        ));
        context.system.addEquation(SketchParametricEquationFactory.circleRadius(
            `${equationId}:radius`,
            radiusParameterId,
            radius,
        ));
        return 'compiled';
    }

    /**
     * 编译目标椭圆与固定外部基础椭圆重合。
     *
     * @param context 编译上下文。
     * @param payload 外部椭圆载荷。
     * @returns 编译结果。
     */
    private compileExternalEllipse(
        context: SketchConstraintFormulaCompileContext,
        payload: object,
    ): SketchConstraintFormulaCompileResult {
        const entityId = context.resolveDirectEntityIds()[0];
        const target = entityId ? context.ellipseGeometryStates.get(entityId) : undefined;
        const center = this.readVector2(Reflect.get(payload, 'center'));
        const majorPositive = this.readVector2(Reflect.get(payload, 'majorPositive'));
        const minorPositive = this.readVector2(Reflect.get(payload, 'minorPositive'));

        if (!target || !center || !majorPositive || !minorPositive) {
            context.reportUnsupported('外部椭圆引用缺少目标椭圆或有效局部轴定义');
            return 'rejected';
        }

        const equationId = `constraint:${context.constraintState.constraintId}`;
        const externalCenter = context.system.addPoint(
            `${equationId}:external-center`,
            center[0],
            center[1],
            true,
        );
        const externalMajor = context.system.addPoint(
            `${equationId}:external-major-positive`,
            majorPositive[0],
            majorPositive[1],
            true,
        );
        const externalMinor = context.system.addPoint(
            `${equationId}:external-minor-positive`,
            minorPositive[0],
            minorPositive[1],
            true,
        );

        context.system.addEquation(SketchParametricEquationFactory.coincident(
            `${equationId}:center`,
            target.center,
            externalCenter,
        ));
        context.system.addEquation(SketchParametricEquationFactory.coincident(
            `${equationId}:major-positive`,
            target.majorPositive,
            externalMajor,
        ));
        context.system.addEquation(SketchParametricEquationFactory.coincident(
            `${equationId}:minor-positive`,
            target.minorPositive,
            externalMinor,
        ));
        return 'compiled';
    }

    /**
     * 编译目标抛物线与固定外部基础抛物线重合。
     *
     * @param context 编译上下文。
     * @param payload 外部抛物线载荷。
     * @returns 编译结果。
     */
    private compileExternalParabola(
        context: SketchConstraintFormulaCompileContext,
        payload: object,
    ): SketchConstraintFormulaCompileResult {
        const entityId = context.resolveDirectEntityIds()[0];
        const target = entityId ? context.parabolaGeometryStates.get(entityId) : undefined;
        const focal = this.readVector2(Reflect.get(payload, 'focal'));
        const apex = this.readVector2(Reflect.get(payload, 'apex'));

        if (!target || !focal || !apex) {
            context.reportUnsupported('外部抛物线引用缺少目标抛物线或有效焦点/顶点');
            return 'rejected';
        }

        const equationId = `constraint:${context.constraintState.constraintId}`;
        const externalFocal = context.system.addPoint(
            `${equationId}:external-focal`,
            focal[0],
            focal[1],
            true,
        );
        const externalApex = context.system.addPoint(
            `${equationId}:external-apex`,
            apex[0],
            apex[1],
            true,
        );

        context.system.addEquation(SketchParametricEquationFactory.coincident(
            `${equationId}:focal`,
            target.focal,
            externalFocal,
        ));
        context.system.addEquation(SketchParametricEquationFactory.coincident(
            `${equationId}:apex`,
            target.apex,
            externalApex,
        ));
        return 'compiled';
    }

    /**
     * 编译目标样条受固定外部参数样条驱动的关系。
     *
     * 中间控制点固定到投影控制点；起终点通过各自曲线参数保持在外部样条上，
     * 与原对象式“中间点固定、端点沿曲线滑动”的可观察语义一致。
     *
     * @param context 编译上下文。
     * @param payload 外部样条载荷。
     * @returns 编译结果。
     */
    private compileExternalSpline(
        context: SketchConstraintFormulaCompileContext,
        payload: object,
    ): SketchConstraintFormulaCompileResult {
        const entityId = context.resolveDirectEntityIds()[0];
        const target = entityId ? context.splineCurveDescriptors.get(entityId) : undefined;
        const rawControlPoints = Reflect.get(payload, 'controlPoints');
        const metadata = Reflect.get(payload, 'curveMetadata');

        if (!target || !Array.isArray(rawControlPoints)
            || !metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
            context.reportUnsupported('外部样条引用缺少可精确求值的目标或投影曲线定义');
            return 'rejected';
        }

        const controlPointValues = rawControlPoints.map((value) => this.readVector2(value));
        // 普通插值样条的编辑拟合点与内核极点不同，关系目标必须优先使用 editPoints。
        const targetEditPoints = target?.editPoints ?? target?.controlPoints ?? [];

        if (controlPointValues.length < 2 || controlPointValues.some((value) => value === null)
            || controlPointValues.length !== targetEditPoints.length) {
            context.reportUnsupported('外部样条控制点数量或局部坐标无效');
            return 'rejected';
        }

        const curveKind = Reflect.get(metadata, 'curveKind');

        if (curveKind !== 'bezier' && curveKind !== 'bspline' && curveKind !== 'spline') {
            context.reportUnsupported('外部内核插值样条尚缺动态极点/节点载荷，禁止用显示折线替代');
            return 'rejected';
        }

        const equationId = `constraint:${context.constraintState.constraintId}`;
        // 编辑点用于约束用户可见控制柄；普通样条的真实极点在下方另行注册。
        const fixedEditPoints = controlPointValues.map((value, index) => context.system.addPoint(
            `${equationId}:external-edit-point:${index}`,
            value![0],
            value![1],
            true,
        ));
        // 显式控制点曲线可直接组装；普通插值样条必须从权威载荷创建。
        const externalSpline = curveKind === 'spline'
            ? this.createExternalInterpolationSpline(context, payload, equationId)
            : this.createExternalControlPointSpline(
                metadata,
                curveKind,
                fixedEditPoints,
            );
        if (!externalSpline) {
            return 'rejected';
        }

        // 中间拟合/控制点继续保持投影位置，仅端点按 OnEdge 语义在曲线上求解。
        targetEditPoints.slice(1, -1).forEach((targetPoint, index) => {
            context.system.addEquation(SketchParametricEquationFactory.coincident(
                `${equationId}:interior-control:${index}`,
                targetPoint,
                fixedEditPoints[index + 1]!,
            ));
        });

        const endpointTargets = [targetEditPoints[0]!, targetEditPoints[targetEditPoints.length - 1]!];

        endpointTargets.forEach((endpoint, index) => {
            const seed = this.resolveNearestSplineParameter(context, externalSpline, endpoint);
            const parameterId = context.system.addCurveParameter(
                `${equationId}:endpoint:${index}`,
                seed,
            );

            context.system.addEquation(SketchParametricEquationFactory.pointOnSpline(
                `${equationId}:endpoint-on-curve:${index}`,
                endpoint,
                parameterId,
                externalSpline,
            ));
        });
        return 'compiled';
    }

    /**
     * 从 Bezier 或显式 B 样条编辑控制点创建固定外部曲线。
     *
     * @param metadata 外部曲线元数据。
     * @param curveKind 外部曲线类型。
     * @param fixedControlPoints 已注册的固定控制点。
     * @returns 可精确求值的外部曲线描述符。
     */
    private createExternalControlPointSpline(
        metadata: object,
        curveKind: 'bezier' | 'bspline',
        fixedControlPoints: readonly SketchParametricPointHandle[],
    ): SketchParametricSplineCurveDescriptor {
        // Bezier/B-spline 的权重与次数直接属于编辑控制点，无需额外内核极点载荷。
        const rawWeights = Reflect.get(metadata, 'weights');
        const rawDegree = Reflect.get(metadata, 'degree');
        const weights = fixedControlPoints.map((_handle, index) => {
            const weight = Array.isArray(rawWeights) ? rawWeights[index] : undefined;

            return typeof weight === 'number' && Number.isFinite(weight) && weight > 0 ? weight : 1;
        });
        const degree = curveKind === 'bezier'
            ? fixedControlPoints.length - 1
            : Math.max(1, Math.min(
                Math.floor(typeof rawDegree === 'number' ? rawDegree : 3),
                fixedControlPoints.length - 1,
            ));

        return {
            kind: curveKind,
            controlPoints: fixedControlPoints,
            weights,
            degree,
            knots: curveKind === 'bspline'
                ? SketchParametricSplineEvaluator.buildClampedUniformKnots(
                    fixedControlPoints.length,
                    degree,
                )
                : [],
            firstParameter: 0,
            lastParameter: 1,
        };
    }

    /**
     * 从 Puppy CAD 内核导出的权威定义创建固定外部插值样条。
     *
     * @param context 公式编译上下文。
     * @param payload 外部样条公式载荷。
     * @param equationId 当前约束方程前缀。
     * @returns 定义有效时返回 B 样条描述符，否则返回 null 并登记 unsupported。
     */
    private createExternalInterpolationSpline(
        context: SketchConstraintFormulaCompileContext,
        payload: object,
        equationId: string,
    ): SketchParametricSplineCurveDescriptor | null {
        const definition = Reflect.get(payload, 'curveEvaluationDefinition');
        if (!definition || typeof definition !== 'object' || Array.isArray(definition)) {
            context.reportUnsupported('外部普通插值样条缺少 Puppy CAD 内核精确求值定义');
            return null;
        }
        // 载荷会跨越持久化与历史恢复边界，进入方程系前必须重新校验结构和数值。
        const rawPoles = Reflect.get(definition, 'controlPoints');
        const rawWeights = Reflect.get(definition, 'weights');
        const rawKnots = Reflect.get(definition, 'knots');
        const rawDegree = Reflect.get(definition, 'degree');
        const firstParameter = Reflect.get(definition, 'firstParameter');
        const lastParameter = Reflect.get(definition, 'lastParameter');
        const periodic = Reflect.get(definition, 'periodic');
        const degree = typeof rawDegree === 'number'
            && Number.isInteger(rawDegree)
            && rawDegree >= 1
            && rawDegree < (Array.isArray(rawPoles) ? rawPoles.length : 0)
            ? rawDegree
            : null;
        if (!Array.isArray(rawPoles) || rawPoles.length < 2
            || !Array.isArray(rawWeights) || rawWeights.length !== rawPoles.length
            || !Array.isArray(rawKnots) || degree === null
            || rawKnots.length !== rawPoles.length + degree + 1
            || typeof firstParameter !== 'number' || !Number.isFinite(firstParameter)
            || typeof lastParameter !== 'number' || !Number.isFinite(lastParameter)
            || lastParameter <= firstParameter
            || typeof periodic !== 'boolean') {
            context.reportUnsupported('外部普通插值样条的极点、权重、节点或参数范围无效');
            return null;
        }
        const poleValues = rawPoles.map((pole) => {
            if (!pole || typeof pole !== 'object') {
                return null;
            }
            return this.readVector2([Reflect.get(pole, 'x'), Reflect.get(pole, 'y')]);
        });
        const weights = rawWeights.map((weight) => (
            typeof weight === 'number' && Number.isFinite(weight) && weight > 0 ? weight : null
        ));
        const knots = rawKnots.map((knot) => (
            typeof knot === 'number' && Number.isFinite(knot) ? knot : null
        ));
        if (poleValues.some((pole) => pole === null)
            || weights.some((weight) => weight === null)
            || knots.some((knot) => knot === null)) {
            context.reportUnsupported('外部普通插值样条包含无效数值');
            return null;
        }
        const numericKnots = knots as number[];
        const hasDescendingKnot = numericKnots.some((knot, index) => (
            index > 0 && knot < numericKnots[index - 1]!
        ));
        const validDomainStart = numericKnots[degree];
        const validDomainEnd = numericKnots[rawPoles.length];
        if (hasDescendingKnot
            || validDomainStart === undefined
            || validDomainEnd === undefined
            || firstParameter < validDomainStart
            || lastParameter > validDomainEnd) {
            context.reportUnsupported('外部普通插值样条的节点顺序或有效参数域无效');
            return null;
        }
        // 外部曲线极点是参考几何快照，必须以固定参数注册，不得参与目标草图自由度求解。
        const poles = poleValues.map((pole, index) => context.system.addPoint(
            `${equationId}:external-pole:${index}`,
            pole![0],
            pole![1],
            true,
        ));

        return {
            kind: 'bspline',
            controlPoints: poles,
            weights: weights as number[],
            degree,
            knots: numericKnots,
            firstParameter,
            lastParameter,
        };
    }

    /**
     * 搜索目标点在固定外部样条上的最近参数 seed。
     *
     * @param context 编译上下文。
     * @param spline 外部样条描述符。
     * @param targetPoint 目标端点。
     * @returns 曲线参数 seed。
     */
    private resolveNearestSplineParameter(
        context: SketchConstraintFormulaCompileContext,
        spline: SketchParametricSplineCurveDescriptor,
        targetPoint: SketchParametricPointHandle,
    ): number {
        const targetX = context.system.read(targetPoint.xId);
        const targetY = context.system.read(targetPoint.yId);
        const span = spline.lastParameter - spline.firstParameter;
        const read = (parameterId: string): number => context.system.read(parameterId);
        let bestParameter = spline.firstParameter;
        let bestDistanceSquared = Number.POSITIVE_INFINITY;

        for (let index = 0; index <= EXTERNAL_SPLINE_SEED_SAMPLE_COUNT; index += 1) {
            const parameter = spline.firstParameter + span * index / EXTERNAL_SPLINE_SEED_SAMPLE_COUNT;

            this.splineEvaluator.evaluatePoint(read, spline, parameter, this.splineSeedPoint);
            const deltaX = this.splineSeedPoint.x - targetX;
            const deltaY = this.splineSeedPoint.y - targetY;
            const distanceSquared = deltaX * deltaX + deltaY * deltaY;

            if (distanceSquared < bestDistanceSquared) {
                bestDistanceSquared = distanceSquared;
                bestParameter = parameter;
            }
        }

        return bestParameter;
    }

    /**
     * 从未知 plain data 读取二维数值元组。
     *
     * @param value 待解析值。
     * @returns 有效二维元组；否则返回 null。
     */
    private readVector2(value: unknown): readonly [number, number] | null {
        if (!Array.isArray(value) || value.length < 2) {
            return null;
        }

        const x = value[0];
        const y = value[1];

        return typeof x === 'number' && Number.isFinite(x)
            && typeof y === 'number' && Number.isFinite(y)
            ? [x, y]
            : null;
    }
}
